// AICallout — ACI intelligence card for the v2 Overview right-rail (CHG-1238).
//
// Surfaces 1-2 "suggested next actions" from the overview API. User cycles
// manually via "Next suggestion ›" (no auto-rotate per design feedback). CTA
// navigates when route is present, else dispatches a forward-promised notice.
//
// Real ACI scoring lands in δ-CHG; this slice ships the surface + accepts
// suggestions verbatim from the API.

import * as LucideIcons from "lucide-react";
import { useCallback, useState, type ComponentType } from "react";
import { useNavigate } from "react-router-dom";
import type { AISuggestion, AISuggestionKind } from "../../api/overview";

type IconCmp = ComponentType<{
  size?: number;
  "aria-hidden"?: boolean | "true";
}>;

// kind → display chip label + tone token (lookups for the locked 10-gradient
// palette). Unknown kinds fall through to a neutral chip.
const KIND_META: Record<string, { label: string; tone: string }> = {
  campaign_opportunity: { label: "Campaign opportunity", tone: "g-events" },
  at_risk_donor: { label: "At-risk donor", tone: "g-people" },
  seasonal_template: { label: "Seasonal template", tone: "g-communications" },
};

function kindMeta(kind: AISuggestionKind) {
  return (
    KIND_META[kind] ?? {
      label: kind.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      tone: "g-home",
    }
  );
}

export interface AICalloutProps {
  suggestions: AISuggestion[];
}

export function AICallout({ suggestions }: AICalloutProps) {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);

  // Hooks must run on every render — derive `current` defensively so the
  // empty-suggestions early return (below) happens AFTER all hook calls.
  const current = suggestions[Math.min(index, Math.max(suggestions.length - 1, 0))];

  const onCta = useCallback(() => {
    if (!current?.cta) return;
    if (current.cta.route) {
      navigate(current.cta.route);
      return;
    }
    setNotice(`${current.cta.label} — coming soon`);
    setTimeout(() => setNotice(null), 1500);
  }, [current?.cta, navigate]);

  const onNext = useCallback(() => {
    setIndex((prev) => (prev + 1) % Math.max(suggestions.length, 1));
    setNotice(null);
  }, [suggestions.length]);

  const { Sparkles, ChevronRight } = LucideIcons as unknown as {
    Sparkles: IconCmp;
    ChevronRight: IconCmp;
  };

  // Callers (DashboardPage's `callout` prop) gate on `suggestions.length > 0`
  // before mounting, so this defensive return is just an extra layer.
  if (suggestions.length === 0 || !current) return null;

  const meta = kindMeta(current.kind);
  const stripeColor = `var(--color-${meta.tone}-from)`;

  return (
    <section
      aria-labelledby="ai-callout-title"
      className="card"
      style={{
        position: "relative",
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        background: "var(--color-surface)",
        borderLeft: `3px solid ${stripeColor}`,
        overflow: "hidden",
      }}
    >
      <header
        style={{ display: "flex", alignItems: "center", gap: 8 }}
      >
        <Sparkles size={14} aria-hidden="true" />
        <span
          aria-hidden="true"
          style={{
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: stripeColor,
            fontWeight: 600,
          }}
        >
          {meta.label}
        </span>
        <span style={{ flex: 1 }} />
        {suggestions.length > 1 && (
          <span
            style={{
              fontSize: 11,
              color: "var(--color-text-muted)",
              fontFamily: "ui-monospace, monospace",
            }}
            aria-label={`Suggestion ${index + 1} of ${suggestions.length}`}
          >
            {index + 1}/{suggestions.length}
          </span>
        )}
      </header>

      <h2
        id="ai-callout-title"
        style={{
          fontSize: 14,
          fontWeight: 500,
          margin: 0,
          color: "var(--color-text)",
          lineHeight: 1.3,
        }}
      >
        {current.title}
      </h2>

      <p
        style={{
          fontSize: 13,
          color: "var(--color-text-secondary)",
          margin: 0,
          lineHeight: 1.45,
        }}
      >
        {current.body}
      </p>

      <footer
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 4,
        }}
      >
        {current.cta && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={onCta}
            data-forward-promised={current.cta.route ? undefined : "true"}
          >
            {current.cta.label}
            {!current.cta.route && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 9,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  background: "rgba(255,255,255,0.18)",
                  padding: "1px 5px",
                  borderRadius: 4,
                }}
              >
                Soon
              </span>
            )}
          </button>
        )}
        <span style={{ flex: 1 }} />
        {suggestions.length > 1 && (
          <button
            type="button"
            onClick={onNext}
            aria-label="Next suggestion"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "6px 10px",
              border: "none",
              background: "transparent",
              color: "var(--color-text-muted)",
              fontSize: 12,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Next suggestion
            <ChevronRight size={14} aria-hidden="true" />
          </button>
        )}
      </footer>

      {notice && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "absolute",
            inset: "auto 12px 12px 12px",
            background: "var(--color-bg-subtle)",
            border: "1px solid var(--color-border-subtle)",
            borderRadius: "var(--radius-sm, 6px)",
            padding: "6px 10px",
            fontSize: 12,
            color: "var(--color-text-secondary)",
            textAlign: "center",
          }}
        >
          {notice}
        </div>
      )}
    </section>
  );
}
